import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Image as ImageIcon, Users, Mail, Wallet, Upload, Trash2, Edit3,
  Plus, X, Check, Loader2, Landmark, Smartphone, FileText, Download, Calendar, ClipboardList,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { BuddhaLogo } from "../components/BuddhaLogo";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const CATEGORIES = ["Education", "Health Camps", "Social Justice", "Community Events"];

const err = (e, fallback) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || fallback);

/* ------------------------- Gallery Tab ------------------------- */
const GalleryTab = () => {
  const [items, setItems] = useState([]);
  const [eventName, setEventName] = useState("");
  const [category, setCategory] = useState("Education");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please choose an image.");
    setUploading(true);
    const fd = new FormData();
    fd.append("event_name", eventName);
    fd.append("category", category);
    fd.append("image", file);
    try {
      await api.post("/gallery", fd);
      toast.success("Image uploaded!");
      setEventName(""); setFile(null);
      document.getElementById("gallery-file").value = "";
      load();
    } catch (e) { err(e, "Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try { await api.delete(`/gallery/${id}`); toast.success("Deleted"); load(); }
    catch (e) { err(e, "Delete failed"); }
  };

  return (
    <div className="space-y-8">
      <form onSubmit={upload} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Upload className="h-5 w-5 text-emerald-600" /> Upload New Photo
        </h3>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label>Event Name</Label>
            <Input value={eventName} onChange={(e) => setEventName(e.target.value)} required
              placeholder="e.g. Blood Donation 2026" data-testid="gallery-event-name-input" className="mt-1.5" />
          </div>
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1.5" data-testid="gallery-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label>Choose Image</Label>
            <Input id="gallery-file" type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])}
              data-testid="gallery-file-input" className="mt-1.5" />
          </div>
        </div>
        <button type="submit" disabled={uploading} data-testid="admin-add-gallery-btn"
          className="mt-5 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold px-6 py-2.5 rounded-full transition-all">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? "Uploading..." : "Upload Image"}
        </button>
      </form>

      <div>
        <h3 className="font-display text-lg font-bold text-slate-900 mb-4">All Photos ({items.length})</h3>
        {items.length === 0 ? (
          <p className="text-slate-400 text-sm">No images uploaded yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((g) => (
              <div key={g.id} className="relative group rounded-xl overflow-hidden border border-slate-200 bg-white">
                <img src={fileUrl(g.image_path)} alt={g.event_name} className="w-full aspect-square object-cover" />
                <div className="p-2.5">
                  <p className="text-sm font-semibold text-slate-800 truncate">{g.event_name}</p>
                  <p className="text-xs text-emerald-700">{g.category}</p>
                </div>
                <button onClick={() => remove(g.id)} data-testid={`gallery-delete-${g.id}`}
                  className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------- Team Tab ------------------------- */
const emptyMember = { name: "", role: "", bio: "", order: 0 };
const TeamTab = () => {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(emptyMember);
  const [file, setFile] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/team").then((r) => setMembers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(emptyMember); setFile(null); setEditing(null);
    const el = document.getElementById("team-file"); if (el) el.value = ""; };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("role", form.role);
    fd.append("bio", form.bio || "");
    fd.append("order", form.order || 0);
    if (file) fd.append("photo", file);
    try {
      if (editing) { await api.put(`/team/${editing}`, fd); toast.success("Member updated"); }
      else { await api.post("/team", fd); toast.success("Member added"); }
      reset(); load();
    } catch (e) { err(e, "Save failed"); }
    finally { setSaving(false); }
  };

  const edit = (m) => {
    setEditing(m.id);
    setForm({ name: m.name, role: m.role, bio: m.bio || "", order: m.order || 0 });
    setFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this member?")) return;
    try { await api.delete(`/team/${id}`); toast.success("Removed"); load(); }
    catch (e) { err(e, "Delete failed"); }
  };

  return (
    <div className="space-y-8">
      <form onSubmit={save} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          {editing ? <Edit3 className="h-5 w-5 text-blue-600" /> : <Plus className="h-5 w-5 text-emerald-600" />}
          {editing ? "Edit Member" : "Add New Member"}
        </h3>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
              placeholder="Full name" data-testid="team-name-input" className="mt-1.5" />
          </div>
          <div>
            <Label>Role</Label>
            <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} required
              placeholder="e.g. Secretary" data-testid="team-role-input" className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Short Bio (optional)</Label>
            <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={2}
              placeholder="One line about this member" data-testid="team-bio-input" className="mt-1.5" />
          </div>
          <div>
            <Label>Display Order</Label>
            <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })}
              className="mt-1.5" />
          </div>
          <div>
            <Label>Photo {editing && "(leave empty to keep current)"}</Label>
            <Input id="team-file" type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])}
              data-testid="team-file-input" className="mt-1.5" />
          </div>
        </div>
        <div className="mt-5 flex gap-3">
          <button type="submit" disabled={saving} data-testid="admin-add-team-btn"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold px-6 py-2.5 rounded-full transition-all">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {editing ? "Update Member" : "Add Member"}
          </button>
          {editing && (
            <button type="button" onClick={reset}
              className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2.5 rounded-full">
              <X className="h-4 w-4" /> Cancel
            </button>
          )}
        </div>
      </form>

      <div>
        <h3 className="font-display text-lg font-bold text-slate-900 mb-4">Team Members ({members.length})</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((m) => (
            <div key={m.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="h-40 bg-gradient-to-br from-blue-50 to-emerald-50 flex items-center justify-center overflow-hidden">
                {m.photo_path ? <img src={fileUrl(m.photo_path)} alt={m.name} className="w-full h-full object-cover" />
                  : <span className="font-display text-2xl font-bold text-[#0F2942]">{m.name.split(" ").map(n=>n[0]).slice(0,2).join("")}</span>}
              </div>
              <div className="p-4">
                <p className="font-semibold text-slate-900">{m.name}</p>
                <p className="text-sm text-emerald-700">{m.role}</p>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => edit(m)} data-testid={`team-edit-${m.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-semibold py-2 rounded-lg">
                    <Edit3 className="h-4 w-4" /> Edit
                  </button>
                  <button onClick={() => remove(m.id)} data-testid={`team-delete-${m.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-semibold py-2 rounded-lg">
                    <Trash2 className="h-4 w-4" /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ------------------------- Messages Tab ------------------------- */
const MessagesTab = () => {
  const [msgs, setMsgs] = useState([]);
  const load = () => api.get("/contact").then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try { await api.delete(`/contact/${id}`); toast.success("Deleted"); load(); }
    catch (e) { err(e, "Delete failed"); }
  };

  return (
    <div>
      <h3 className="font-display text-lg font-bold text-slate-900 mb-4">Contact Messages ({msgs.length})</h3>
      {msgs.length === 0 ? <p className="text-slate-400 text-sm">No messages yet.</p> : (
        <div className="space-y-4">
          {msgs.map((m) => (
            <div key={m.id} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex justify-between items-start gap-3">
                <div>
                  <p className="font-semibold text-slate-900">{m.name}</p>
                  <p className="text-sm text-slate-500">{m.email}{m.phone && ` · ${m.phone}`}</p>
                </div>
                <button onClick={() => remove(m.id)} data-testid={`message-delete-${m.id}`}
                  className="text-red-500 hover:bg-red-50 p-2 rounded-lg"><Trash2 className="h-4 w-4" /></button>
              </div>
              {m.subject && <p className="mt-2 text-sm font-medium text-slate-700">Subject: {m.subject}</p>}
              <p className="mt-2 text-sm text-slate-600 whitespace-pre-wrap bg-slate-50 rounded-lg p-3">{m.message}</p>
              <p className="mt-2 text-xs text-slate-400">{new Date(m.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------- Donations Tab ------------------------- */
const emptyUpi = { method_type: "upi", upi_id: "", display_name: "" };
const emptyBank = { method_type: "bank", bank_name: "", account_holder: "", account_number: "", ifsc: "", branch: "", account_type: "Current" };

const DonationsTab = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyUpi);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/donations").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setType = (t) => { setForm(t === "upi" ? emptyUpi : emptyBank); setEditing(null); };
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/donations/${editing}`, form); toast.success("Updated"); }
      else { await api.post("/donations", form); toast.success("Added"); }
      setForm(form.method_type === "upi" ? emptyUpi : emptyBank); setEditing(null); load();
    } catch (e) { err(e, "Save failed"); }
  };

  const edit = (d) => { setForm({ ...d }); setEditing(d.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (id) => {
    if (!window.confirm("Delete this payment detail?")) return;
    try { await api.delete(`/donations/${id}`); toast.success("Deleted"); load(); }
    catch (e) { err(e, "Delete failed"); }
  };

  return (
    <div className="space-y-8">
      <form onSubmit={save} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Wallet className="h-5 w-5 text-emerald-600" /> {editing ? "Edit" : "Add"} Payment Detail
        </h3>
        <div className="flex gap-3 mb-5">
          <button type="button" onClick={() => setType("upi")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${form.method_type === "upi" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}>
            <Smartphone className="h-4 w-4" /> UPI
          </button>
          <button type="button" onClick={() => setType("bank")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${form.method_type === "bank" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}>
            <Landmark className="h-4 w-4" /> Bank
          </button>
        </div>
        {form.method_type === "upi" ? (
          <div className="grid sm:grid-cols-2 gap-5">
            <div><Label>UPI ID</Label><Input value={form.upi_id} onChange={f("upi_id")} required placeholder="name@bank" className="mt-1.5" data-testid="donation-upi-input" /></div>
            <div><Label>Payee Name</Label><Input value={form.display_name} onChange={f("display_name")} placeholder="Foundation name" className="mt-1.5" /></div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5">
            <div><Label>Bank Name</Label><Input value={form.bank_name} onChange={f("bank_name")} required className="mt-1.5" /></div>
            <div><Label>Account Holder</Label><Input value={form.account_holder} onChange={f("account_holder")} required className="mt-1.5" /></div>
            <div><Label>Account Number</Label><Input value={form.account_number} onChange={f("account_number")} required className="mt-1.5" /></div>
            <div><Label>IFSC Code</Label><Input value={form.ifsc} onChange={f("ifsc")} required className="mt-1.5" /></div>
            <div><Label>Branch</Label><Input value={form.branch} onChange={f("branch")} className="mt-1.5" /></div>
            <div><Label>Account Type</Label><Input value={form.account_type} onChange={f("account_type")} className="mt-1.5" /></div>
          </div>
        )}
        <div className="mt-5 flex gap-3">
          <button type="submit" data-testid="donation-save-btn"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-2.5 rounded-full">
            <Check className="h-4 w-4" /> {editing ? "Update" : "Add"}
          </button>
          {editing && <button type="button" onClick={() => { setForm(emptyUpi); setEditing(null); }}
            className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 font-semibold px-6 py-2.5 rounded-full"><X className="h-4 w-4" /> Cancel</button>}
        </div>
      </form>

      <div className="space-y-4">
        {items.map((d) => (
          <div key={d.id} className="bg-white rounded-xl border border-slate-200 p-5 flex justify-between items-start gap-4">
            <div className="text-sm">
              {d.method_type === "upi" ? (
                <><p className="font-semibold text-slate-900 flex items-center gap-2"><Smartphone className="h-4 w-4 text-emerald-600" /> UPI</p>
                  <p className="text-slate-600 mt-1">{d.upi_id}</p><p className="text-slate-400">{d.display_name}</p></>
              ) : (
                <><p className="font-semibold text-slate-900 flex items-center gap-2"><Landmark className="h-4 w-4 text-emerald-600" /> {d.bank_name}</p>
                  <p className="text-slate-600 mt-1">{d.account_holder} · A/C {d.account_number}</p>
                  <p className="text-slate-400">IFSC {d.ifsc} · {d.branch} · {d.account_type}</p></>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => edit(d)} data-testid={`donation-edit-${d.id}`} className="text-blue-600 hover:bg-blue-50 p-2 rounded-lg"><Edit3 className="h-4 w-4" /></button>
              <button onClick={() => remove(d.id)} data-testid={`donation-delete-${d.id}`} className="text-red-500 hover:bg-red-50 p-2 rounded-lg"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ------------------------- Home Slides Tab ------------------------- */
const SlidesTab = () => {
  const [slides, setSlides] = useState([]);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/hero-slides").then((r) => setSlides(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please choose an image.");
    setUploading(true);
    const fd = new FormData();
    fd.append("image", file);
    fd.append("title", title);
    fd.append("subtitle", subtitle);
    fd.append("order", slides.length + 1);
    try {
      await api.post("/hero-slides", fd);
      toast.success("Slide added to home page!");
      setTitle(""); setSubtitle(""); setFile(null);
      document.getElementById("slide-file").value = "";
      load();
    } catch (e) { err(e, "Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this slide?")) return;
    try { await api.delete(`/hero-slides/${id}`); toast.success("Slide deleted"); load(); }
    catch (e) { err(e, "Delete failed"); }
  };

  return (
    <div className="space-y-8">
      <form onSubmit={upload} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
          <Upload className="h-5 w-5 text-emerald-600" /> Add Home Page Slide
        </h3>
        <p className="text-sm text-slate-500 mb-4">These photos rotate in the big banner on the home page. Maximum 8 slides ({slides.length}/8 used).</p>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label>Title (optional)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Health Camp 2026" data-testid="slide-title-input" className="mt-1.5" />
          </div>
          <div>
            <Label>Subtitle (optional)</Label>
            <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)}
              placeholder="Short line under the title" data-testid="slide-subtitle-input" className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Choose Image *</Label>
            <Input id="slide-file" type="file" accept="image/*" required onChange={(e) => setFile(e.target.files[0])}
              data-testid="slide-file-input" className="mt-1.5" />
          </div>
        </div>
        <button type="submit" disabled={uploading || slides.length >= 8} data-testid="admin-add-slide-btn"
          className="mt-5 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold px-6 py-2.5 rounded-full transition-all">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? "Uploading..." : "Add Slide"}
        </button>
      </form>

      <div>
        <h3 className="font-display text-lg font-bold text-slate-900 mb-4">Current Slides ({slides.length})</h3>
        {slides.length === 0 ? (
          <p className="text-slate-400 text-sm">No custom slides yet — the home page shows the default banner image.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {slides.map((s, i) => (
              <div key={s.id} className="relative group rounded-xl overflow-hidden border border-slate-200 bg-white">
                <img src={fileUrl(s.image_path)} alt={s.title || `Slide ${i + 1}`} className="w-full aspect-video object-cover" />
                <span className="absolute top-2 left-2 bg-[#0F2942]/90 text-white text-[11px] font-bold px-2.5 py-1 rounded-full">Slide {i + 1}</span>
                {s.title && <div className="p-2.5"><p className="text-sm font-semibold text-slate-800 truncate">{s.title}</p></div>}
                <button onClick={() => remove(s.id)} data-testid={`slide-delete-${s.id}`}
                  className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------- Documents Tab ------------------------- */
const DocumentsTab = () => {
  const [docs, setDocs] = useState([]);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please choose a file.");
    setUploading(true);
    const fd = new FormData();
    fd.append("name", name);
    fd.append("file", file);
    try {
      await api.post("/documents", fd);
      toast.success("Document uploaded!");
      setName(""); setFile(null);
      document.getElementById("doc-file").value = "";
      load();
    } catch (e) { err(e, "Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); load(); }
    catch (e) { err(e, "Delete failed"); }
  };

  return (
    <div className="space-y-8">
      <form onSubmit={upload} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
          <Upload className="h-5 w-5 text-emerald-600" /> Upload New Document
        </h3>
        <p className="text-sm text-slate-500 mb-4">PDF, Word, Excel, TXT, CSV or image files. These appear on the public Documents page.</p>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label>Document Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required
              placeholder="e.g. Registration Certificate" data-testid="doc-name-input" className="mt-1.5" />
          </div>
          <div>
            <Label>Choose File</Label>
            <Input id="doc-file" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,image/*" required
              onChange={(e) => setFile(e.target.files[0])} data-testid="doc-file-input" className="mt-1.5" />
          </div>
        </div>
        <button type="submit" disabled={uploading} data-testid="admin-add-doc-btn"
          className="mt-5 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold px-6 py-2.5 rounded-full transition-all">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? "Uploading..." : "Upload Document"}
        </button>
      </form>

      <div>
        <h3 className="font-display text-lg font-bold text-slate-900 mb-4">Uploaded Documents ({docs.length})</h3>
        {docs.length === 0 ? (
          <p className="text-slate-400 text-sm">No documents uploaded yet.</p>
        ) : (
          <div className="space-y-3">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-4 bg-white rounded-xl border border-slate-200 p-4">
                <div className="h-11 w-11 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                  <FileText className="h-5 w-5 text-blue-700" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 truncate">{d.name}</p>
                  <p className="text-xs text-slate-400">{d.original_filename} · {new Date(d.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <a href={`/api/documents/${d.id}/download`} target="_blank" rel="noreferrer"
                    data-testid={`doc-download-${d.id}`}
                    className="inline-flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-semibold px-3.5 py-2 rounded-lg">
                    <Download className="h-4 w-4" /> <span className="hidden sm:inline">View</span>
                  </a>
                  <button onClick={() => remove(d.id)} data-testid={`doc-delete-${d.id}`}
                    className="inline-flex items-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-semibold px-3.5 py-2 rounded-lg">
                    <Trash2 className="h-4 w-4" /> <span className="hidden sm:inline">Delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------- Events Tab ------------------------- */
const emptyEvent = { title: "", description: "", date: "", status: "upcoming" };
const EventsTab = () => {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(emptyEvent);
  const [file, setFile] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(emptyEvent); setFile(null); setEditing(null);
    const el = document.getElementById("event-file"); if (el) el.value = ""; };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("description", form.description || "");
    fd.append("date", form.date);
    fd.append("status", form.status);
    if (file) fd.append("image", file);
    try {
      if (editing) { await api.put(`/events/${editing}`, fd); toast.success("Event updated"); }
      else { await api.post("/events", fd); toast.success("Event added"); }
      reset(); load();
    } catch (e) { err(e, "Save failed"); }
    finally { setSaving(false); }
  };

  const edit = (ev) => {
    setEditing(ev.id);
    setForm({ title: ev.title, description: ev.description || "", date: ev.date, status: ev.status });
    setFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const markPast = async (ev) => {
    const fd = new FormData();
    fd.append("status", "past");
    try { await api.put(`/events/${ev.id}`, fd); toast.success("Marked as past event"); load(); }
    catch (e) { err(e, "Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try { await api.delete(`/events/${id}`); toast.success("Deleted"); load(); }
    catch (e) { err(e, "Delete failed"); }
  };

  return (
    <div className="space-y-8">
      <form onSubmit={save} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          {editing ? <Edit3 className="h-5 w-5 text-blue-600" /> : <Plus className="h-5 w-5 text-emerald-600" />}
          {editing ? "Edit Event" : "Add New Event"}
        </h3>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label>Event Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required
              placeholder="e.g. Free Health Camp" data-testid="event-title-input" className="mt-1.5" />
          </div>
          <div>
            <Label>Date</Label>
            <Input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required
              placeholder="e.g. 15 August 2026" data-testid="event-date-input" className="mt-1.5" />
          </div>
          <div className="sm:col-span-2">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
              placeholder="What is this event about?" data-testid="event-desc-input" className="mt-1.5" />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="mt-1.5" data-testid="event-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="upcoming">Upcoming Event</SelectItem>
                <SelectItem value="past">Past Event</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Photo {editing && "(leave empty to keep current)"}</Label>
            <Input id="event-file" type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])}
              data-testid="event-file-input" className="mt-1.5" />
          </div>
        </div>
        <div className="mt-5 flex gap-3">
          <button type="submit" disabled={saving} data-testid="admin-add-event-btn"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-semibold px-6 py-2.5 rounded-full transition-all">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {editing ? "Update Event" : "Add Event"}
          </button>
          {editing && <button type="button" onClick={reset}
            className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 font-semibold px-6 py-2.5 rounded-full"><X className="h-4 w-4" /> Cancel</button>}
        </div>
      </form>

      <div>
        <h3 className="font-display text-lg font-bold text-slate-900 mb-4">All Events ({events.length})</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((ev) => (
            <div key={ev.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="h-36 bg-gradient-to-br from-blue-50 to-emerald-50 flex items-center justify-center overflow-hidden relative">
                {ev.image_path ? <img src={fileUrl(ev.image_path)} alt={ev.title} className="w-full h-full object-cover" />
                  : <Calendar className="h-10 w-10 text-slate-300" />}
                <span className={`absolute top-2 left-2 text-white text-[10px] font-bold px-2.5 py-1 rounded-full ${ev.status === "upcoming" ? "bg-emerald-600" : "bg-slate-500"}`}>
                  {ev.status === "upcoming" ? "Upcoming" : "Past"}
                </span>
              </div>
              <div className="p-4">
                <p className="text-xs text-emerald-700 font-semibold">{ev.date}</p>
                <p className="font-semibold text-slate-900 mt-0.5">{ev.title}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => edit(ev)} data-testid={`event-edit-${ev.id}`}
                    className="inline-flex items-center gap-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1.5 rounded-lg">
                    <Edit3 className="h-3.5 w-3.5" /> Edit
                  </button>
                  {ev.status === "upcoming" && (
                    <button onClick={() => markPast(ev)} data-testid={`event-markpast-${ev.id}`}
                      className="inline-flex items-center gap-1 bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs font-semibold px-3 py-1.5 rounded-lg">
                      <Check className="h-3.5 w-3.5" /> Mark Past
                    </button>
                  )}
                  <button onClick={() => remove(ev.id)} data-testid={`event-delete-${ev.id}`}
                    className="inline-flex items-center gap-1 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold px-3 py-1.5 rounded-lg">
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ------------------------- Registrations Tab ------------------------- */
const RegistrationsTab = () => {
  const [regs, setRegs] = useState([]);
  const load = () => api.get("/event-registrations").then((r) => setRegs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this registration?")) return;
    try { await api.delete(`/event-registrations/${id}`); toast.success("Deleted"); load(); }
    catch (e) { err(e, "Delete failed"); }
  };

  return (
    <div>
      <h3 className="font-display text-lg font-bold text-slate-900 mb-4">Event Registrations ({regs.length})</h3>
      {regs.length === 0 ? <p className="text-slate-400 text-sm">No registrations yet.</p> : (
        <div className="overflow-x-auto bg-white rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Phone</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Event</th>
                <th className="px-4 py-3 font-semibold">Note</th>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {regs.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`registration-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{r.name}</td>
                  <td className="px-4 py-3 text-slate-600">{r.phone}</td>
                  <td className="px-4 py-3 text-slate-600">{r.email || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{r.event_title}</td>
                  <td className="px-4 py-3 text-slate-500 max-w-[180px] truncate">{r.message || "-"}</td>
                  <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => remove(r.id)} data-testid={`registration-delete-${r.id}`}
                      className="text-red-500 hover:bg-red-50 p-2 rounded-lg"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ------------------------- Dashboard Shell ------------------------- */
const AdminDashboardPage = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0F2942] text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BuddhaLogo className="h-10 w-10" />
            <div>
              <p className="font-display font-bold leading-tight">Admin Dashboard</p>
              <p className="text-xs text-slate-400">Lord Buddha Awareness & Development Foundation</p>
            </div>
          </div>
          <button onClick={doLogout} data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-4 py-2 rounded-full text-sm">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="slides">
          <TabsList className="mb-8 flex-wrap h-auto">
            <TabsTrigger value="slides" data-testid="admin-tab-slides" className="gap-1.5"><ImageIcon className="h-4 w-4" /> Home Slides</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="admin-tab-gallery" className="gap-1.5"><ImageIcon className="h-4 w-4" /> Gallery</TabsTrigger>
            <TabsTrigger value="documents" data-testid="admin-tab-documents" className="gap-1.5"><FileText className="h-4 w-4" /> Documents</TabsTrigger>
            <TabsTrigger value="events" data-testid="admin-tab-events" className="gap-1.5"><Calendar className="h-4 w-4" /> Events</TabsTrigger>
            <TabsTrigger value="registrations" data-testid="admin-tab-registrations" className="gap-1.5"><ClipboardList className="h-4 w-4" /> Registrations</TabsTrigger>
            <TabsTrigger value="team" data-testid="admin-tab-team" className="gap-1.5"><Users className="h-4 w-4" /> Team</TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages" className="gap-1.5"><Mail className="h-4 w-4" /> Messages</TabsTrigger>
            <TabsTrigger value="donations" data-testid="admin-tab-donations" className="gap-1.5"><Wallet className="h-4 w-4" /> Donations</TabsTrigger>
          </TabsList>
          <TabsContent value="slides"><SlidesTab /></TabsContent>
          <TabsContent value="gallery"><GalleryTab /></TabsContent>
          <TabsContent value="documents"><DocumentsTab /></TabsContent>
          <TabsContent value="events"><EventsTab /></TabsContent>
          <TabsContent value="registrations"><RegistrationsTab /></TabsContent>
          <TabsContent value="team"><TeamTab /></TabsContent>
          <TabsContent value="messages"><MessagesTab /></TabsContent>
          <TabsContent value="donations"><DonationsTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
